/**
 * Browser CustomEvent contract for training-alerts contextual nudge triggers.
 */

export const TRAINING_ALERTS_NUDGE_EVENT = 'apt:training-alerts-nudge';

export const TRAINING_ALERTS_NUDGE_TRIGGERS = [
  'events_engagement',
  'events_empty',
  'event_exit',
  'homepage_engagement',
] as const;

export type TrainingAlertsNudgeTrigger =
  (typeof TRAINING_ALERTS_NUDGE_TRIGGERS)[number];

export type TrainingAlertsNudgeAcceptMode = 'modal' | 'alerts';

export type TrainingAlertsNudgeDetail = {
  trigger: TrainingAlertsNudgeTrigger;
  acceptMode: TrainingAlertsNudgeAcceptMode;
  geoStateId?: number | null;
  countyIds?: number[];
  guestSource?: string;
};

export function isTrainingAlertsNudgeTrigger(
  value: unknown,
): value is TrainingAlertsNudgeTrigger {
  return (
    typeof value === 'string' &&
    (TRAINING_ALERTS_NUDGE_TRIGGERS as readonly string[]).includes(value)
  );
}

export function isTrainingAlertsNudgeDetail(
  value: unknown,
): value is TrainingAlertsNudgeDetail {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const detail = value as Record<string, unknown>;
  if (!isTrainingAlertsNudgeTrigger(detail.trigger)) {
    return false;
  }
  if (detail.acceptMode !== 'modal' && detail.acceptMode !== 'alerts') {
    return false;
  }
  if (
    detail.geoStateId !== undefined &&
    detail.geoStateId !== null &&
    typeof detail.geoStateId !== 'number'
  ) {
    return false;
  }
  if (
    detail.countyIds != null &&
    (!Array.isArray(detail.countyIds) ||
      !detail.countyIds.every((id) => typeof id === 'number'))
  ) {
    return false;
  }
  if (detail.guestSource != null && typeof detail.guestSource !== 'string') {
    return false;
  }
  return true;
}

export function dispatchTrainingAlertsNudge(
  detail: TrainingAlertsNudgeDetail,
  target: Window | Document | EventTarget = typeof window !== 'undefined'
    ? window
    : (undefined as unknown as EventTarget),
): boolean {
  if (!target || typeof CustomEvent === 'undefined') {
    return false;
  }
  target.dispatchEvent(
    new CustomEvent(TRAINING_ALERTS_NUDGE_EVENT, {
      detail,
      bubbles: true,
    }),
  );
  return true;
}
